import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useTranslation } from 'react-i18next';
import { tones } from '../../theme/tokens';

const ICONS = {
  error: ErrorOutlineRoundedIcon,
  warning: WarningAmberRoundedIcon,
  info: InfoOutlinedIcon,
  success: CheckCircleOutlineRoundedIcon,
};

/**
 * A coloured message that stays next to what it is about (a form, a card,
 * a table) until the cause is fixed. Use it instead of alert() for
 * validation and for problems that need reading; field-level errors go in
 * the field's own helperText.
 *
 *   <InlineMessage kind="error" title="Could not save">{message}</InlineMessage>
 *   <InlineMessage kind="warning" actionLabel="Add money" onAction={...}>Balance too low.</InlineMessage>
 *
 * kind: 'error' | 'warning' | 'info' | 'success' (default 'info').
 */
export default function InlineMessage({ kind = 'info', title, children, actionLabel, onAction, onClose, icon, id, sx }) {
  const { t } = useTranslation();
  const tone = tones[kind] || tones.info;
  const Icon = ICONS[kind] || ICONS.info;
  return (
    <Box
      id={id}
      role={kind === 'error' ? 'alert' : 'status'}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        p: 1.5,
        borderRadius: '12px',
        bgcolor: tone.bg,
        color: tone.fg,
        fontSize: 14,
        lineHeight: 1.5,
        ...sx,
      }}
    >
      <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', pt: '1px', flex: 'none' }}>
        {icon || <Icon sx={{ fontSize: 20 }} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
        {title ? <Box component="div" sx={{ fontWeight: 700 }}>{title}</Box> : null}
        {children ? <Box component="div">{children}</Box> : null}
      </Box>
      {actionLabel && typeof onAction === 'function' ? (
        <Button
          variant="text"
          onClick={onAction}
          sx={{ color: tone.fg, fontWeight: 700, flex: 'none', my: -0.75, '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' } }}
        >
          {actionLabel}
        </Button>
      ) : null}
      {typeof onClose === 'function' ? (
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={t('close_message', { defaultValue: 'Close message' })}
          sx={{ color: tone.fg, flex: 'none', my: -0.5 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Box>
  );
}
